import React, { useState, useEffect } from 'react';
import app, { auth, db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where } from 'firebase/firestore';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, Chip, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function Billing() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [athleteData, setAthleteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [subscriptionActionLoading, setSubscriptionActionLoading] = useState(false);

  const [invoices, setInvoices] = useState(null);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState('');

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (currentUser) => {
      if (!currentUser) { navigate('/login'); return; }
      setUser(currentUser);
      try {
        const athleteSnap = await getDocs(query(
          collection(db, 'athletes'),
          where('email', '==', currentUser.email)
        ));
        const ownAthlete = athleteSnap.docs.find(d => d.data().athleteType?.toLowerCase() === 'adult');
        if (ownAthlete) setAthleteData({ id: ownAthlete.id, ...ownAthlete.data() });
      } catch (err) {
        setError(`Couldn't load billing data: ${err.message}`);
      }
      setLoading(false);
    });
    return unsub;
  }, [navigate]);

  useEffect(() => {
    if (tab === 2 && athleteData?.stripeCustomerId && invoices === null && !invoicesLoading) {
      loadInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, athleteData?.stripeCustomerId]);

  const loadInvoices = async () => {
    if (!athleteData?.id) return;
    setInvoicesLoading(true);
    setInvoicesError('');
    try {
      const functions = getFunctions(app, 'us-central1');
      const listFn = httpsCallable(functions, 'listInvoices');
      const result = await listFn({ athleteId: athleteData.id });
      setInvoices(result.data?.invoices || []);
    } catch (err) {
      setInvoicesError(`Couldn't load invoices: ${err.message || err.code || 'Unknown error'}`);
      setInvoices([]);
    }
    setInvoicesLoading(false);
  };

  const handleManageSubscription = async () => {
    if (!athleteData?.id) return;
    try {
      const functions = getFunctions(app, 'us-central1');
      const portalSession = httpsCallable(functions, 'createBillingPortalSession');
      const result = await portalSession({ athleteId: athleteData.id, returnUrl: window.location.href });
      const url = result.data?.url;
      if (url) window.location.href = url;
      else setError('Portal session created but no URL returned.');
    } catch (err) {
      setError(`Couldn't open billing portal: ${err.message || err.code || 'Unknown error'}`);
    }
  };

  const handleCancelSubscription = async () => {
    if (!athleteData?.id || !athleteData?.stripeSubscriptionId) return;
    setSubscriptionActionLoading(true);
    setError('');
    try {
      const functions = getFunctions(app, 'us-central1');
      const cancelFn = httpsCallable(functions, 'cancelSubscription');
      await cancelFn({ subscriptionId: athleteData.stripeSubscriptionId, athleteId: athleteData.id });
      setSuccess('Subscription scheduled to cancel at the end of the current billing period.');
      setAthleteData(prev => prev ? { ...prev, subscriptionCancelAtPeriodEnd: true } : prev);
      setCancelConfirmOpen(false);
    } catch (err) {
      setError(`Couldn't cancel subscription: ${err.message || err.code || 'Unknown error'}`);
    }
    setSubscriptionActionLoading(false);
  };

  const handleResumeSubscription = async () => {
    if (!athleteData?.id || !athleteData?.stripeSubscriptionId) return;
    setSubscriptionActionLoading(true);
    setError('');
    try {
      const functions = getFunctions(app, 'us-central1');
      const resumeFn = httpsCallable(functions, 'resumeSubscription');
      await resumeFn({ subscriptionId: athleteData.stripeSubscriptionId, athleteId: athleteData.id });
      setSuccess('Subscription resumed. Your membership will continue billing as normal.');
      setAthleteData(prev => prev ? { ...prev, subscriptionCancelAtPeriodEnd: false, subscriptionCancelAt: null } : prev);
    } catch (err) {
      setError(`Couldn't resume subscription: ${err.message || err.code || 'Unknown error'}`);
    }
    setSubscriptionActionLoading(false);
  };

  if (loading) {
    return <Layout><Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box></Layout>;
  }

  if (!athleteData?.stripeCustomerId) {
    return (
      <Layout>
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 860, mx: 'auto' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Billing</Typography>
          <Paper elevation={2} sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: '#888', mb: 2 }}>
              You don't have any billing history yet.
            </Typography>
            <Typography variant="body2" sx={{ color: '#aaa' }}>
              Once you make a purchase, your subscription, payment methods, and invoices will appear here.
            </Typography>
          </Paper>
        </Box>
      </Layout>
    );
  }

  const subStatus = athleteData?.subscriptionStatus;
  const cancelingAtPeriodEnd = athleteData?.subscriptionCancelAtPeriodEnd === true;
  const subActive = subStatus === 'active' || subStatus === 'trialing';

  let endDateLabel = '';
  if (cancelingAtPeriodEnd && athleteData.subscriptionCancelAt) {
    const ts = athleteData.subscriptionCancelAt;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (!isNaN(d)) endDateLabel = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } else if (subActive && athleteData.subscriptionCurrentPeriodEnd) {
    const ts = athleteData.subscriptionCurrentPeriodEnd;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (!isNaN(d)) endDateLabel = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 860, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E3A5F' }}>Billing</Typography>
          <Button size="small" onClick={() => navigate('/my-account')} sx={{ color: '#888' }}>
            ← Back to Account
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Paper elevation={2} sx={{ borderRadius: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #eee', px: 2 }} variant="scrollable" scrollButtons="auto">
            <Tab label="Subscription" />
            <Tab label="Payment Method" />
            <Tab label="Invoices" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            <TabPanel value={tab} index={0}>
              {!subStatus && (
                <Typography variant="body1" sx={{ color: '#888' }}>You don't have an active subscription.</Typography>
              )}
              {subStatus && (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Your Subscription</Typography>
                  <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#f9f9f9', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="caption" sx={{ color: '#888' }}>Status</Typography>
                      <Chip
                        label={
                          cancelingAtPeriodEnd ? 'Canceling' :
                          subStatus === 'active' ? 'Active' :
                          subStatus === 'trialing' ? 'Trial' :
                          subStatus === 'past_due' ? 'Past Due' :
                          subStatus === 'canceled' ? 'Canceled' :
                          subStatus
                        }
                        size="small"
                        sx={{
                          backgroundColor:
                            cancelingAtPeriodEnd ? '#E67E22' :
                            subStatus === 'active' || subStatus === 'trialing' ? '#27AE60' :
                            subStatus === 'past_due' ? '#E67E22' :
                            '#888',
                          color: 'white', fontWeight: 600, fontSize: '0.7rem',
                        }}
                      />
                    </Box>
                    {endDateLabel && (
                      <Typography variant="body2" sx={{ color: '#555', mt: 0.5 }}>
                        {cancelingAtPeriodEnd
                          ? `Membership expires on ${endDateLabel}. You'll keep full access until then.`
                          : `Renews on ${endDateLabel}.`}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2 }}>
                    {subActive && !cancelingAtPeriodEnd && (
                      <Button variant="outlined" color="error" disabled={subscriptionActionLoading}
                        onClick={() => setCancelConfirmOpen(true)}>
                        Cancel Subscription
                      </Button>
                    )}
                    {cancelingAtPeriodEnd && (
                      <Button variant="contained" disabled={subscriptionActionLoading}
                        onClick={handleResumeSubscription}
                        sx={{ backgroundColor: '#27AE60', '&:hover': { backgroundColor: '#229954' } }}>
                        {subscriptionActionLoading ? 'Resuming…' : 'Resume Subscription'}
                      </Button>
                    )}
                    <Button variant="text" onClick={handleManageSubscription} sx={{ color: '#888', fontSize: '0.85rem' }}>
                      Manage in Stripe portal
                    </Button>
                  </Box>

                  <Dialog open={cancelConfirmOpen} onClose={() => setCancelConfirmOpen(false)}>
                    <DialogTitle>Cancel Subscription?</DialogTitle>
                    <DialogContent>
                      <Typography variant="body2" sx={{ color: '#555' }}>
                        {endDateLabel
                          ? `Your subscription will be canceled at the end of the current billing period on ${endDateLabel}. You'll keep full access until then. After that date, you won't be billed again.`
                          : `Your subscription will be canceled at the end of the current billing period. You'll keep full access until then.`}
                      </Typography>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setCancelConfirmOpen(false)} disabled={subscriptionActionLoading}>
                        Keep Subscription
                      </Button>
                      <Button onClick={handleCancelSubscription} disabled={subscriptionActionLoading}
                        color="error" variant="contained">
                        {subscriptionActionLoading ? 'Canceling…' : 'Yes, Cancel'}
                      </Button>
                    </DialogActions>
                  </Dialog>
                </Box>
              )}
            </TabPanel>

            <TabPanel value={tab} index={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Payment Method</Typography>
              <Typography variant="body2" sx={{ color: '#888' }}>
                In-app payment method updates coming soon. For now, you can update your card via the Stripe portal.
              </Typography>
              <Button variant="outlined" onClick={handleManageSubscription} sx={{ mt: 2, borderColor: '#1E3A5F', color: '#1E3A5F' }}>
                Open Stripe portal
              </Button>
            </TabPanel>

            <TabPanel value={tab} index={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F' }}>Invoices</Typography>
                <Button size="small" onClick={loadInvoices} disabled={invoicesLoading} sx={{ color: '#1E3A5F' }}>
                  {invoicesLoading ? 'Loading…' : 'Refresh'}
                </Button>
              </Box>

              {invoicesError && <Alert severity="error" sx={{ mb: 2 }}>{invoicesError}</Alert>}

              {invoicesLoading && invoices === null && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
              )}

              {invoices !== null && invoices.length === 0 && !invoicesLoading && (
                <Typography variant="body2" sx={{ color: '#aaa' }}>No invoices yet.</Typography>
              )}

              {invoices !== null && invoices.length > 0 && (
                <Box>
                  {invoices.map((inv, i) => {
                    const date = inv.created ? new Date(inv.created * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
                    const amount = `$${((inv.amountPaid || inv.amountDue || 0) / 100).toFixed(2)}`;
                    const statusColor =
                      inv.status === 'paid' ? '#27AE60' :
                      inv.status === 'open' ? '#E67E22' :
                      inv.status === 'void' ? '#888' :
                      inv.status === 'uncollectible' ? '#E74C3C' :
                      '#888';
                    const statusLabel =
                      inv.status === 'paid' ? 'Paid' :
                      inv.status === 'open' ? 'Unpaid' :
                      inv.status === 'void' ? 'Void' :
                      inv.status === 'uncollectible' ? 'Uncollectible' :
                      inv.status === 'draft' ? 'Draft' :
                      (inv.status || '—');
                    return (
                      <Box key={inv.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2, borderRadius: 2, backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white', mb: 0.5, gap: 2 }}>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{date}</Typography>
                          {inv.description && (
                            <Typography variant="caption" sx={{ color: '#888', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {inv.description}
                            </Typography>
                          )}
                          {inv.number && (
                            <Typography variant="caption" sx={{ color: '#aaa' }}>#{inv.number}</Typography>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{amount}</Typography>
                          <Chip label={statusLabel} size="small"
                                sx={{ backgroundColor: statusColor, color: 'white', fontWeight: 600, fontSize: '0.65rem', height: 18, mt: 0.5 }} />
                        </Box>
                        {inv.hostedInvoiceUrl && (
                          <Button size="small" variant="text" href={inv.hostedInvoiceUrl}
                                  target="_blank" rel="noopener noreferrer"
                                  sx={{ color: '#1E3A5F', fontSize: '0.75rem', flexShrink: 0 }}>
                            View
                          </Button>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </TabPanel>
          </Box>
        </Paper>
      </Box>
    </Layout>
  );
}

export default Billing;
